import { ORG_SUBSCRIPTION_PLANS, TRIAL_CHANGE_EFFECTIVE_DATE } from "../constants/billing";
import { useAppStore } from "./useStores";

export interface ApiLimitStatus {
	orgUsage: number;
	freeLimit: number;
	freeLimitWarningThreshold: number;
	isAboutToBreakLimit: boolean;
	isPassedFreeLimit: boolean;
	trialAboutToEnd: boolean;
	trialEnded: boolean;
	isUserCreatedBeforeGrowthDate: boolean;
	remainingTrialInDays: number;
}

export const useApiLimitStatus = (): ApiLimitStatus => {
	const { authStore } = useAppStore();

	// --- Usage & limit ---
	const orgUsage = authStore.orgUsage?.apiUsage ?? 0;
	const freeLimit = authStore.orgUsage?.apiFreeCallsLimit ?? 100;
	const freeTrialDays = authStore.orgUsage?.freeTrialDays ?? 14;
	const freeLimitWarningThreshold = freeLimit * 0.8; // 80% of limit reached

	// --- Trial configuration ---
	const TRIAL_WARNING_DAYS = Math.floor(freeTrialDays * 0.8); // 11 days

	// --- Calculate trial progress ---
	const orgCreatedAtDate = authStore.orgSubscription?.org_created_at
		? new Date(authStore.orgSubscription.org_created_at)
		: null;
	const isUserCreatedBeforeGrowthDate =
		orgCreatedAtDate == null ? false : orgCreatedAtDate < TRIAL_CHANGE_EFFECTIVE_DATE;
	let trialDaysPassed = 0;
	let remainingTrialInDays = 14;

	if (isUserCreatedBeforeGrowthDate && orgCreatedAtDate != null && authStore.orgUsage) {
		const now = new Date();
		const MS_PER_DAY = 24 * 60 * 60 * 1000;
		trialDaysPassed = Math.floor((now.getTime() - orgCreatedAtDate.getTime()) / MS_PER_DAY);
		remainingTrialInDays = freeTrialDays - trialDaysPassed;
	}

	const trialAboutToEnd = trialDaysPassed >= TRIAL_WARNING_DAYS && trialDaysPassed < freeTrialDays;
	const trialEnded = trialDaysPassed > freeTrialDays && authStore.orgPassedFreeLimit;

	// --- Status flags ---
	const isAboutToBreakLimit = (orgUsage >= freeLimitWarningThreshold && orgUsage < freeLimit) || trialAboutToEnd;
	const isPayingOrg = authStore.orgSubscription?.plan_name !== ORG_SUBSCRIPTION_PLANS.free.name;
	const isPassedFreeLimit = (authStore.orgPassedFreeLimit || isAboutToBreakLimit || trialEnded) && !isPayingOrg;

	return {
		isUserCreatedBeforeGrowthDate,
		orgUsage,
		freeLimit,
		freeLimitWarningThreshold,
		isAboutToBreakLimit,
		isPassedFreeLimit,
		trialAboutToEnd,
		trialEnded,
		remainingTrialInDays,
	};
};
